import { supabase } from "@/integrations/supabase/client";
import { toast } from "@/components/ui/use-toast";
import { sanitizeText } from "@/utils/sanitize";

export interface ClientFormData {
  id?: string;
  name: string;
  document_type: "cpf" | "cnpj";
  document: string;
  email?: string;
  phone?: string;
  notes?: string;
  address?: {
    street?: string;
    number?: string;
    complement?: string;
    neighborhood?: string;
    city?: string;
    state?: string;
    zipCode?: string;
  };
}

export async function createClient(clientData: ClientFormData) {
  try {
    const sanitizedData = {
      ...clientData,
      name: sanitizeText(clientData.name),
      notes: clientData.notes ? sanitizeText(clientData.notes) : clientData.notes,
    };

    const { data, error } = await supabase.from("clients").insert({
      ...sanitizedData,
      user_id: (await supabase.auth.getUser()).data.user?.id,
    }).select();

    if (error) {
      toast({
        title: "Erro ao cadastrar cliente",
        description: error.message,
        variant: "destructive",
      });
      return { success: false, error };
    }

    toast({
      title: "Cliente cadastrado com sucesso!",
      description: "O cliente foi adicionado à sua base de dados.",
    });
    return { success: true, data };
  } catch (error: any) {
    toast({
      title: "Erro ao cadastrar cliente",
      description: error.message,
      variant: "destructive",
    });
    return { success: false, error };
  }
}

export async function updateClient(clientData: ClientFormData) {
  try {
    const { data, error } = await supabase
      .from("clients")
      .update({
        name: sanitizeText(clientData.name),
        document_type: clientData.document_type,
        document: clientData.document,
        email: clientData.email,
        phone: clientData.phone,
        address: clientData.address,
        notes: clientData.notes ? sanitizeText(clientData.notes) : clientData.notes,
        updated_at: new Date().toISOString(), // Convert Date to string
      })

      .eq("id", clientData.id)
      .select();

    if (error) {
      toast({
        title: "Erro ao atualizar cliente",
        description: error.message,
        variant: "destructive",
      });
      return { success: false, error };
    }

    toast({
      title: "Cliente atualizado com sucesso!",
      description: "As informações do cliente foram atualizadas.",
    });
    return { success: true, data };
  } catch (error: any) {
    toast({
      title: "Erro ao atualizar cliente",
      description: error.message,
      variant: "destructive",
    });
    return { success: false, error };
  }
}

export async function archiveClient(clientId: string) {
  try {
    const { data, error } = await supabase
      .from("clients")
      .update({ archived: true })
      .eq("id", clientId)
      .select();

    if (error) {
      toast({
        title: "Erro ao arquivar cliente",
        description: error.message,
        variant: "destructive",
      });
      return { success: false, error };
    }

    toast({
      title: "Cliente arquivado com sucesso!",
      description: "O cliente foi arquivado.",
    });
    return { success: true, data };
  } catch (error: any) {
    toast({
      title: "Erro ao arquivar cliente",
      description: error.message,
      variant: "destructive",
    });
    return { success: false, error };
  }
}

export async function getClients(includeArchived = false) {
  try {
    let query = supabase
      .from("clients")
      .select("*")
      .order("name");
    
    if (!includeArchived) {
      query = query.eq("archived", false);
    }

    const { data, error } = await query;

    if (error) {
      console.error("Erro ao buscar clientes:", error);
      return { success: false, error, data: [] };
    }

    return { success: true, data };
  } catch (error: any) {
    console.error("Erro ao buscar clientes:", error);
    return { success: false, error, data: [] };
  }
}

export async function getClientById(clientId: string) {
  try {
    const { data, error } = await supabase
      .from("clients")
      .select("*")
      .eq("id", clientId)
      .maybeSingle();

    if (error) {
      console.error("Erro ao buscar cliente:", error);
      return { success: false, error, data: null };
    }

    return { success: true, data };
  } catch (error: any) {
    console.error("Erro ao buscar cliente:", error);
    return { success: false, error, data: null };
  }
}
